import { all, put, takeEvery } from "redux-saga/effects";
import { checkAccountAvailability } from "../../Services/api";
import {
  logout,
  otpRequest,
  otpValidate,
  refreshAccessToken,
  signInWithAuthCredentials
} from "../../Services/authentication";
import { updateAccessSyncStatus } from "../../Services/database";
import { throwError } from "../../Services/error";
import { setErrorStatus } from "../status/action";
import store from "../store";
import { login } from "./actions";
import { isValidObject } from "../../Services/validators";
import { isLocalHost } from "../../Utils/constants";
import * as Sentry from "@sentry/react";

export const authActionTypes = {
  SEND_OTP: "SEND_OTP",
  VERIFY_OTP: "VERIFY_OTP",
  LOGIN: "LOGIN",
  LOGOUT: "LOGOUT",
  ADD_ACCESS_TOKEN: "ADD_ACCESS_TOKEN",
  ADD_AUTH_INFO: "ADD_AUTH_INFO",
  CLEAR_CREDENTIAL: "CLEAR_CREDENTIAL",
  REFRESH_ACCESS_TOKEN: "REFRESH_ACCESS_TOKEN",
  FORCE_RETRY_API: "FORCE_RETRY_API",
  SET_THEME: "SET_THEME"
};

function* loginWorker() {
  try {
    yield setAuthLoading(true);
    const uid = store.getState().auth.data.uid;
    const profile = store.getState().profile.data;
    if (uid !== null && profile !== null) {
      throw throwError("custom", "User already logged in");
    }
    yield signInWithAuthCredentials(
      store.getState().auth.credentials.authCredential
    );
    yield setAuthLoading(false);
  } catch (error) {
    yield setErrorStatus(error, "loginWorker");
    yield setAuthLoading(false);
  }
}

function* logoutWorker(action) {
  try {
    yield setAuthLoading(true);
    if (!isLocalHost()) {
      Sentry.logger.debug(
        `Logout successfully - ${
          store.getState().auth?.data?.phoneNumber || null
        }`
      );
    }
    yield logout();
    yield put({
      type: "RESET"
    });
    // Raised AFTER RESET so the status reducer's RESET branch can't wipe it.
    if (action?.payload?.status) {
      yield setErrorStatus(action.payload.status, "logoutWorker");
    }
  } catch (error) {
    yield setAuthLoading(false);
    yield setErrorStatus(error, "logoutWorker");
    yield setAuthLoading(false);
  }
}

function* sendOtpWorker(action) {
  try {
    yield setAuthLoading(true);
    if (action.payload.type === "login") {
      const response = yield checkAccountAvailability(
        action.payload.phoneNumber
      );
      if (
        response?.data?.moduleAccess?.employee !== true &&
        response?.data?.moduleAccess?.independentEngineer !== true
      ) {
        throw throwError("custom", "User does not exist. Please signup");
      }
    }
    const verificationId = yield otpRequest(action.payload.phoneNumber);

    yield put({
      type: "SET_AUTH_VERIFICATION_ID",
      payload: {
        verificationId
      }
    });

    yield setAuthLoading(false);
  } catch (error) {
    yield setErrorStatus(error, "sendOtpWorker");
    yield setAuthLoading(false);
  }
}

function* verifyOtpWorker(action) {
  try {
    yield setAuthLoading(true);
    const authCredential = yield otpValidate(
      action.payload.loginOtp,
      store.getState().auth.credentials.verificationId
    );

    yield put({
      type: "SET_AUTH_CREDENTIALS",
      payload: {
        authCredential
      }
    });
    login();
    yield setAuthLoading(false);
  } catch (error) {
    yield setErrorStatus(error, "verifyOtpWorker");
  }
}

function* clearCredentialWorker() {
  yield put({
    type: "CLEAR_AUTH_CREDENTIALS"
  });
}

function* setThemeWorker(action) {
  try {
    yield put({
      type: "PUT_THEME",
      payload: {
        theme: action.payload.theme
      }
    });
  } catch (error) {}
}

function* addAccessTokenWorker(action) {
  try {
    yield put({
      type: "SET_ACCESS_TOKEN",
      payload: {
        accessToken: action.payload.accessToken
      }
    });
  } catch (error) {
    yield setErrorStatus(error);
  }
}

function* addAuthInfoWorker(action) {
  try {
    yield put({
      type: "SET_AUTH_INFO",
      payload: action.payload
    });
  } catch (error) {
    yield setErrorStatus(error);
  }
}

function* accessTokenRefreshWorker(action) {
  try {
    const accessToken = yield refreshAccessToken();
    yield put({
      type: "SET_AUTH_TOKEN",
      payload: {
        ...store.getState().auth.data,
        accessToken
      }
    });

    if (action.payload.shouldUpdateAccessStatus) {
      yield updateAccessSyncStatus(
        store.getState().profile.data?.employeeId,
        action.payload.shouldUpdateAccessStatus
      );
      console.warn(
        "accessSyncStatus updated to:",
        action.payload.shouldUpdateAccessStatus
      );
    }
  } catch (error) {
    yield setErrorStatus(error, "accessTokenRefreshWorker");
    console.error("error from accessTokenRefreshWorker : ", error);
  }
}

function* forceRetryAPIWorker(action) {
  try {
    const refreshedAccessToken = yield refreshAccessToken();
    const data = action.payload.data;
    if (!isValidObject(data)) {
      return;
    }
    const { url, method, headers, body } = data;

    const retryHeaders = new Headers(headers);
    retryHeaders.set("x-auth-token", `Bearer ${refreshedAccessToken}`);

    const getHeader = (headersObj, key) =>
      Object.entries(headersObj).find(
        ([k]) => k.toLowerCase() === key.toLowerCase()
      )?.[1];

    const contentType = getHeader(headers, "content-type");

    let retryBody;

    if (body && method !== "GET" && method !== "HEAD") {
      if (contentType.includes("application/json")) {
        retryBody = JSON.stringify(body);
      } else if (contentType.includes("multipart/form-data")) {
        const formData = new FormData();

        for (const key in body) {
          const value = body[key];

          if (
            typeof value === "object" &&
            value !== null &&
            "name" in value &&
            "type" in value &&
            "buffer" in value
          ) {
            const byteArray = new Uint8Array(value.buffer);
            const fileBlob = new Blob([byteArray], { type: value.type });
            const realFile = new File([fileBlob], value.name, {
              type: value.type
            });

            formData.append(key, realFile);
          } else {
            formData.append(key, value);
          }
        }

        retryBody = formData;

        retryHeaders.delete("Content-Type");
      }
    }

    const retryInit = {
      method,
      headers: retryHeaders,
      body: retryBody
    };

    const retryResponse = yield fetch(url, retryInit);

    if (retryResponse.ok) {
      const pathname = new URL(url).pathname;

      if (pathname.includes("/faceCompare")) {
        navigator.serviceWorker.controller?.postMessage(
          "SHIFT_NEXT_FACE_REQUEST"
        );
      } else if (pathname.includes("/procedureHistory")) {
        navigator.serviceWorker.controller?.postMessage(
          "SHIFT_NEXT_PROCEDURE_HISTORY_REQUEST"
        );
      }
    }
  } catch (error) {
    yield setErrorStatus(error, "forceRetryAPIWorker");
  }
}

export default function* authWatcher() {
  yield all([
    takeEvery("SEND_OTP", sendOtpWorker),
    takeEvery("VERIFY_OTP", verifyOtpWorker),
    takeEvery("SET_THEME", setThemeWorker),
    takeEvery("FORCE_RETRY_API", forceRetryAPIWorker),
    takeEvery("LOGIN", loginWorker),
    takeEvery("LOGOUT", logoutWorker),
    takeEvery("ADD_ACCESS_TOKEN", addAccessTokenWorker),
    takeEvery("ADD_AUTH_INFO", addAuthInfoWorker),
    takeEvery("CLEAR_CREDENTIAL", clearCredentialWorker),
    takeEvery("REFRESH_ACCESS_TOKEN", accessTokenRefreshWorker)
  ]);
}

function* setAuthLoading(bool) {
  yield put({
    type: "SET_AUTH_LOADING",
    payload: {
      loading: bool
    }
  });
}

// {
//   type: "SET_ACCESS_TOKEN",
//   payload: {
//     accessToken: "eyJhbGciOiJSUzI1NiIsImtpZCI6ImE4ZGY2MmQzYTBhNDRlM2RmY2RjYWZjNmRhMTM4Mzc3NDU5ZjliMDEiLCJ0eXAiOiJKV1QifQ.eyJtb2R1bGVBY2Nlc3MiOnsiZW1wbG95ZWUiOnRydWV9LCJjb21wbGFpbnRzIjp7InVwZGF0ZSI6eyJ0YWtlb3ZlciI6dHJ1ZX19LCJwcm9jZWR1cmVzIjp7InJlYWQiOnRydWV9LCJlbXBsb3llZXMiOnsicmVhZCI6dHJ1ZX0sInN1cnZlaWxsYW5jZSI6eyJ1cGRhdGUiOnRydWUsInJlYWQiOnRydWV9LCJpc3MiOiJodHRwczovL3NlY3VyZXRva2VuLmdvb2dsZS5jb20vcGN0LWRldi0zODAxMDUiLCJhdWQiOiJwY3QtZGV2LTM4MDEwNSIsImF1dGhfdGltZSI6MTc1MjE0OTc3OCwidXNlcl9pZCI6IlROSTNpUUk2QzdWVmFUS2tNMWwyOG9xQ2NzbjIiLCJzdWIiOiJUTkkzaVFJNkM3VlZhVEtrTTFsMjhvcUNjc24yIiwiaWF0IjoxNzUzMDcyNjk0LCJleHAiOjE3NTMwNzYyOTQsInBob25lX251bWJlciI6Iis5MTg4MjU3MjY3MTciLCJmaXJlYmFzZSI6eyJpZGVudGl0aWVzIjp7InBob25lIjpbIis5MTg4MjU3MjY3MTciXX0sInNpZ25faW5fcHJvdmlkZXIiOiJwaG9uZSJ9fQ.hMpy2Et9hA6kOlrKpolzhUXmLf58aad6Co-K3AJH0yf7JXXfDj_mRXDXFTHiMiqeNrM3_VNowQYZjL2nl_WCZeAKZ15bKEMrXRnL6bQYhrVwnEAa6zR2ebaAjANmM38sPkrreBsGehjGn-EQ42DIt5lSQcEKkR-tptlm4-1GEgk7nGUajVTTFkTCdmHYCOTg9EhI4xHrKA6la2jSvUXQHLvxwZgxpw6BTTQMS6Ps6PHj6dApiW7rkcUteR7BiD6lJzAyfFkX-rxPHQHpikdO6cMXJfLIALqj5aciuXHx7aqOwX6oFJ0S6ecmnT72p1DqV4hRY9XeYI4eokhi2EsUoA"
//     }
// }
